import React from "react";
import {
  AlertTriangle,
  BadgeEuro,
  Building2,
  Calculator,
  CheckCircle2,
  FileCheck2,
  FileText,
  Hash,
  Landmark,
  PiggyBank,
  Plus,
  ShieldCheck,
  Trash2,
  Wallet,
  ChevronDown,
} from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./LenderManualLoanCaseFinalLoanDetails.css";

/* =========================================================
   EMPTY STATE
   ========================================================= */

const EMPTY_FINAL_LOAN_DETAILS = {
  decision: "APPROVED",

  approved_total_loan_amount: "",
  approved_purchase_loan_amount: "",
  approved_renovation_loan_amount: "",

  property_price: "",
  own_contribution_amount: "",
  first_tranche_amount: "",

  tenure_years: "",
  indicative_apr_pct: "",
  estimated_monthly_payment: "",

  mortgage_amount: "",
  mortgage_mandate_amount: "",

  renovation_financing_status: "ESTIMATED",
  renovation_financing_notes: "",

  conditions: "",
  notes: "",

  funding_sources: [],
};

const PERCENT_FIELDS = new Set(["indicative_apr_pct"]);
const INTEGER_FIELDS = new Set(["tenure_years"]);

/* =========================================================
   GENERIC HELPERS
   ========================================================= */

function isObject(value) {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function toNumber(value) {
  if (!hasValue(value)) return null;

  const parsed = Number(String(value).replace(/\s/g, "").replace(/,/g, "."));

  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeDecimal(value = "", maximumDecimals = 2) {
  const raw = String(value ?? "")
    .replace(/,/g, ".")
    .replace(/[^\d.]/g, "");

  if (!raw) return "";

  const firstDecimalIndex = raw.indexOf(".");

  if (firstDecimalIndex === -1) {
    return raw.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  }

  const integerPart = raw
    .slice(0, firstDecimalIndex)
    .replace(/\D/g, "")
    .replace(/^0+(?=\d)/, "");

  const decimalPart = raw
    .slice(firstDecimalIndex + 1)
    .replace(/\D/g, "")
    .slice(0, maximumDecimals);

  if (raw.endsWith(".") && decimalPart.length === 0) {
    return `${integerPart || "0"}.`;
  }

  return decimalPart.length
    ? `${integerPart || "0"}.${decimalPart}`
    : integerPart || "0";
}

function normalizeInteger(value = "") {
  return String(value ?? "")
    .replace(/\D/g, "")
    .replace(/^0+(?=\d)/, "");
}

function sanitizeFormattedNumber(value = "", maximumDecimals = 2) {
  const raw = String(value ?? "").replace(/[^\d.,]/g, "");

  if (!raw) return "";

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");
  const decimalIndex = Math.max(lastComma, lastDot);

  if (decimalIndex === -1) {
    return raw.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  }

  const integerPart = raw
    .slice(0, decimalIndex)
    .replace(/\D/g, "")
    .replace(/^0+(?=\d)/, "");

  const decimalPart = raw
    .slice(decimalIndex + 1)
    .replace(/\D/g, "")
    .slice(0, maximumDecimals);

  if ((raw.endsWith(".") || raw.endsWith(",")) && !decimalPart) {
    return `${integerPart || "0"}.`;
  }

  return decimalPart
    ? `${integerPart || "0"}.${decimalPart}`
    : integerPart || "0";
}

function sanitizeMoneyInput(value = "") {
  const raw = String(value ?? "");

  if (!raw) {
    return "";
  }

  /*
   * Commas in this component are DISPLAY thousands
   * separators, so remove them before parsing.
   *
   * 140,000     -> 140000
   * 140,000.50  -> 140000.50
   */
  const withoutThousandsSeparators = raw.replace(/,/g, "");

  /*
   * Allow only digits and one decimal point.
   */
  const cleaned = withoutThousandsSeparators.replace(/[^\d.]/g, "");

  const firstDecimalIndex = cleaned.indexOf(".");

  if (firstDecimalIndex === -1) {
    return cleaned.replace(/^0+(?=\d)/, "");
  }

  const integerPart = cleaned
    .slice(0, firstDecimalIndex)
    .replace(/\D/g, "")
    .replace(/^0+(?=\d)/, "");

  const decimalPart = cleaned
    .slice(firstDecimalIndex + 1)
    .replace(/\D/g, "")
    .slice(0, 2);

  if (cleaned.endsWith(".") && decimalPart.length === 0) {
    return `${integerPart || "0"}.`;
  }

  return decimalPart.length
    ? `${integerPart || "0"}.${decimalPart}`
    : integerPart || "0";
}

function formatMoneyInput(value = "") {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const text = String(value);

  const normalized = text.replace(/,/g, "");

  if (normalized.endsWith(".")) {
    const integerPart = normalized.slice(0, -1);

    const integerNumber = Number(integerPart || 0);

    if (!Number.isFinite(integerNumber)) {
      return "";
    }

    return `${integerNumber.toLocaleString("en-US")}.`;
  }

  const parts = normalized.split(".");

  const integerPart = parts[0] || "0";

  const decimalPart = parts.length > 1 ? parts[1] : null;

  const integerNumber = Number(integerPart);

  if (!Number.isFinite(integerNumber)) {
    return "";
  }

  const formattedInteger = integerNumber.toLocaleString("en-US");

  if (decimalPart !== null) {
    return `${formattedInteger}.${decimalPart}`;
  }

  return formattedInteger;
}

function emptyToNull(value, maximumDecimals = 2) {
  return hasValue(value) ? normalizeDecimal(value, maximumDecimals) : null;
}

function formatCurrency(value, locale = "en-GB") {
  const numeric = toNumber(value);

  if (numeric === null) return "—";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function calculateMonthlyPayment(principalValue, aprValue, tenureYearsValue) {
  const principal = toNumber(principalValue);
  const annualRate = toNumber(aprValue);
  const tenureYears = toNumber(tenureYearsValue);

  if (
    principal === null ||
    principal <= 0 ||
    annualRate === null ||
    annualRate < 0 ||
    tenureYears === null ||
    tenureYears <= 0
  ) {
    return null;
  }

  const numberOfPayments = Math.round(tenureYears * 12);
  if (numberOfPayments <= 0) return null;

  const monthlyRate = annualRate / 100 / 12;

  if (monthlyRate === 0) {
    return principal / numberOfPayments;
  }

  return (
    (principal * monthlyRate) /
    (1 - Math.pow(1 + monthlyRate, -numberOfPayments))
  );
}

/* =========================================================
   MANUAL-ENTRY CONTEXT
   ========================================================= */

function getFinancialInfo(loanCaseData, stepValues) {
  const candidates = [
    stepValues?.FINANCIAL?.financial_info,
    stepValues?.financial?.financial_info,
    stepValues?.["financial-info"]?.financial_info,
    stepValues?.["financial-info"],
    loanCaseData?.financial_info,
    loanCaseData?.financing_details,
    loanCaseData?.dossier?.financing_details,
  ];

  return candidates.find((candidate) => isObject(candidate)) || {};
}

function getPropertyInfo(loanCaseData, stepValues) {
  const candidates = [
    stepValues?.PROPERTY?.property_info,
    stepValues?.property?.property_info,
    stepValues?.["property-info"]?.property_info,
    stepValues?.["property-info"],
    loanCaseData?.property_info,
    loanCaseData?.property,
    loanCaseData?.linked_property,
    loanCaseData?.dossier?.property_info,
  ];

  return candidates.find((candidate) => isObject(candidate)) || {};
}

function getCountryCode(loanCaseData, propertyInfo) {
  const candidates = [
    propertyInfo?.country_code,
    propertyInfo?.country,
    loanCaseData?.country_code,
    loanCaseData?.dossier?.country_code,
    loanCaseData?.dossier?.country_of_property_purchase,
  ];

  const raw = candidates.find((value) => hasValue(value));
  const normalized = String(raw || "")
    .trim()
    .toUpperCase();

  if (["BELGIUM", "BELGIË", "BELGIQUE"].includes(normalized)) {
    return "BE";
  }

  return normalized;
}

function getMarketRegionLabel(loanCaseData) {
  return (
    loanCaseData?.dossier?.market_region?.name ||
    loanCaseData?.dossier?.market_region_name ||
    loanCaseData?.dossier?.region_label ||
    loanCaseData?.market_region?.name ||
    ""
  );
}

function getMarketRegionCode(loanCaseData) {
  return String(
    loanCaseData?.dossier?.market_region?.code ||
      loanCaseData?.dossier?.market_region_code ||
      loanCaseData?.market_region?.code ||
      "",
  )
    .trim()
    .toUpperCase();
}

function getLenderReference(loanCaseData) {
  return (
    loanCaseData?.lender_external_reference ||
    loanCaseData?.loan_case?.lender_external_reference ||
    loanCaseData?.case_reference ||
    loanCaseData?.loan_case?.case_reference ||
    ""
  );
}

function getContextNumber(financialInfo, ...keys) {
  for (const key of keys) {
    const value = financialInfo?.[key];
    const parsed = toNumber(value);

    if (parsed !== null) return parsed;
  }

  return 0;
}

/* =========================================================
   FUNDING SOURCES
   ========================================================= */

function normalizeFundingSource(source, index) {
  return {
    client_key:
      source?.client_key ||
      (source?.id ? `saved-${source.id}` : `source-${index}-${Date.now()}`),
    source_type: String(source?.source_type || "").toUpperCase(),
    name: String(source?.name || ""),
    amount: source?.amount == null ? "" : String(source.amount),
    status: String(source?.status || "EXPECTED").toUpperCase(),
  };
}

function serializeFundingSources(fundingSources) {
  return fundingSources
    .filter((source) =>
      Boolean(source.source_type || source.name || hasValue(source.amount)),
    )
    .map((source) => ({
      source_type: String(source.source_type || "")
        .trim()
        .toUpperCase(),
      name: String(source.name || "").trim(),
      amount: emptyToNull(source.amount, 2),
      status: String(source.status || "EXPECTED")
        .trim()
        .toUpperCase(),
    }));
}

function normalizeFundingCatalogEntry(item, index) {
  if (!isObject(item)) return null;

  const category = String(
    item.category || item.source_type || item.funding_type || "",
  )
    .trim()
    .toUpperCase();

  const name = String(
    item.name || item.label || item.financing_product || "",
  ).trim();

  if (!name) return null;

  let sourceType = "OTHER";

  if (["SUBSIDY", "GRANT"].includes(category)) {
    sourceType = "SUBSIDY";
  } else if (category === "LOAN" && item.is_lender_financing !== true) {
    sourceType = "SOFT_LOAN";
  } else if (category === "OTHER") {
    sourceType = "OTHER";
  } else if (["SUBSIDY", "SOFT_LOAN", "OTHER"].includes(category)) {
    sourceType = category;
  } else {
    return null;
  }

  return {
    id: item.id ?? `catalog-${index}`,
    code: String(item.code || ""),
    name,
    source_type: sourceType,
    category,
    sort_order: Number(item.sort_order || 0),
  };
}

function getFundingCatalog(loanCaseData) {
  const candidates = [
    loanCaseData?.available_funding_source_types,
    loanCaseData?.available_funding_sources,
    loanCaseData?.funding_source_types,
    loanCaseData?.dossier?.available_funding_source_types,
    loanCaseData?.dossier?.funding_source_types,
    loanCaseData?.market_region?.funding_source_types,
    loanCaseData?.dossier?.market_region?.funding_source_types,
  ];

  const raw = candidates.find(Array.isArray) || [];

  return raw
    .filter(
      (item) =>
        item &&
        item.is_active !== false &&
        item.selectable_by_lender !== false &&
        item.is_lender_financing !== true,
    )
    .map(normalizeFundingCatalogEntry)
    .filter(Boolean)
    .sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
    );
}

function getFundingNameOptions({ sourceType, fundingCatalog, isFlandersCase }) {
  if (!sourceType || sourceType === "OTHER") return [];

  const configured = fundingCatalog
    .filter((item) => item.source_type === sourceType)
    .map((item) => ({ value: item.name, label: item.name }));

  if (configured.length) return configured;

  if (isFlandersCase && sourceType === "SUBSIDY") {
    return [{ value: "Mijn VerbouwPremie", label: "Mijn VerbouwPremie" }];
  }

  if (isFlandersCase && sourceType === "SOFT_LOAN") {
    return [{ value: "Mijn Verbouwlening", label: "Mijn Verbouwlening" }];
  }

  return [];
}

/* =========================================================
   NORMALIZATION
   ========================================================= */

function normalizeFinalLoanDetails(source, fundingSourcesSource = []) {
  const value = isObject(source) ? source : {};

  const rawFundingSources = Array.isArray(value.funding_sources)
    ? value.funding_sources
    : Array.isArray(fundingSourcesSource)
      ? fundingSourcesSource
      : [];

  return {
    ...EMPTY_FINAL_LOAN_DETAILS,
    ...value,
    decision: "APPROVED",

    approved_total_loan_amount:
      value.approved_total_loan_amount == null
        ? ""
        : String(value.approved_total_loan_amount),

    approved_purchase_loan_amount:
      value.approved_purchase_loan_amount == null
        ? ""
        : String(value.approved_purchase_loan_amount),

    approved_renovation_loan_amount:
      value.approved_renovation_loan_amount == null
        ? ""
        : String(value.approved_renovation_loan_amount),

    property_price:
      value.property_price == null ? "" : String(value.property_price),

    own_contribution_amount:
      value.own_contribution_amount == null
        ? ""
        : String(value.own_contribution_amount),

    first_tranche_amount:
      value.first_tranche_amount == null
        ? ""
        : String(value.first_tranche_amount),

    tenure_years: value.tenure_years == null ? "" : String(value.tenure_years),

    indicative_apr_pct:
      value.indicative_apr_pct == null ? "" : String(value.indicative_apr_pct),

    estimated_monthly_payment:
      value.estimated_monthly_payment == null
        ? ""
        : String(value.estimated_monthly_payment),

    mortgage_amount:
      value.mortgage_amount == null ? "" : String(value.mortgage_amount),

    mortgage_mandate_amount:
      value.mortgage_mandate_amount == null
        ? ""
        : String(value.mortgage_mandate_amount),

    renovation_financing_status: String(
      value.renovation_financing_status || "ESTIMATED",
    ).toUpperCase(),

    renovation_financing_notes: value.renovation_financing_notes || "",
    conditions: value.conditions || "",
    notes: value.notes || "",

    funding_sources: rawFundingSources.map(normalizeFundingSource),
  };
}

function serializeFinalLoanDetails(details) {
  return {
    decision: "APPROVED",

    approved_total_loan_amount: emptyToNull(
      details.approved_total_loan_amount,
      2,
    ),
    approved_purchase_loan_amount: emptyToNull(
      details.approved_purchase_loan_amount,
      2,
    ),
    approved_renovation_loan_amount: emptyToNull(
      details.approved_renovation_loan_amount,
      2,
    ),

    property_price: emptyToNull(details.property_price, 2),
    own_contribution_amount: emptyToNull(details.own_contribution_amount, 2),
    first_tranche_amount: emptyToNull(details.first_tranche_amount, 2),

    tenure_years: hasValue(details.tenure_years)
      ? Number(normalizeInteger(details.tenure_years))
      : null,

    indicative_apr_pct: emptyToNull(details.indicative_apr_pct, 4),
    estimated_monthly_payment: emptyToNull(
      details.estimated_monthly_payment,
      2,
    ),

    mortgage_amount: emptyToNull(details.mortgage_amount, 2),
    mortgage_mandate_amount: emptyToNull(details.mortgage_mandate_amount, 2),

    renovation_financing_status:
      details.renovation_financing_status || "ESTIMATED",
    renovation_financing_notes: String(
      details.renovation_financing_notes || "",
    ).trim(),

    conditions: String(details.conditions || "").trim(),
    notes: String(details.notes || "").trim(),

    funding_sources: serializeFundingSources(details.funding_sources),

    rejection_reason_code: "",
    rejection_details: "",
    allow_reapplication: false,
    reapply_after: "",
  };
}

/* =========================================================
   VALIDATION
   ========================================================= */

function buildValidation({
  details,
  isBelgiumCase,
  renovationBudget,
  totalProjectCost,
}) {
  const fieldErrors = {};
  const fieldWarnings = {};
  const fundingSourceErrors = {};

  const required = [
    "approved_total_loan_amount",
    "approved_purchase_loan_amount",
    "approved_renovation_loan_amount",
    "property_price",
    "own_contribution_amount",
    "tenure_years",
    "indicative_apr_pct",
    "estimated_monthly_payment",
  ];

  required.forEach((field) => {
    if (!hasValue(details[field])) {
      fieldErrors[field] = "validation.required";
    }
  });

  [
    "approved_total_loan_amount",
    "approved_purchase_loan_amount",
    "approved_renovation_loan_amount",
    "property_price",
    "tenure_years",
    "estimated_monthly_payment",
  ].forEach((field) => {
    if (hasValue(details[field]) && (toNumber(details[field]) || 0) <= 0) {
      fieldErrors[field] = "validation.greaterThanZero";
    }
  });

  if (
    hasValue(details.indicative_apr_pct) &&
    (toNumber(details.indicative_apr_pct) ?? -1) < 0
  ) {
    fieldErrors.indicative_apr_pct = "validation.zeroOrGreater";
  }

  if (
    hasValue(details.own_contribution_amount) &&
    (toNumber(details.own_contribution_amount) ?? -1) < 0
  ) {
    fieldErrors.own_contribution_amount = "validation.zeroOrGreater";
  }

  if (
    hasValue(details.first_tranche_amount) &&
    (toNumber(details.first_tranche_amount) ?? -1) < 0
  ) {
    fieldErrors.first_tranche_amount = "validation.zeroOrGreater";
  }

  const approvedTotal = toNumber(details.approved_total_loan_amount);
  const purchaseLoan = toNumber(details.approved_purchase_loan_amount);
  const renovationLoan = toNumber(details.approved_renovation_loan_amount);

  if (
    approvedTotal !== null &&
    purchaseLoan !== null &&
    renovationLoan !== null &&
    Math.abs(approvedTotal - (purchaseLoan + renovationLoan)) > 0.01
  ) {
    fieldErrors.approved_total_loan_amount = "validation.approvedTotalMismatch";
  }

  if (
    hasValue(details.tenure_years) &&
    (!Number.isInteger(toNumber(details.tenure_years)) ||
      (toNumber(details.tenure_years) || 0) <= 0)
  ) {
    fieldErrors.tenure_years = "validation.invalidTenure";
  }

  if (isBelgiumCase) {
    if (!hasValue(details.mortgage_amount)) {
      fieldErrors.mortgage_amount = "validation.required";
    }

    if (!hasValue(details.mortgage_mandate_amount)) {
      fieldErrors.mortgage_mandate_amount = "validation.required";
    }

    const mortgage = toNumber(details.mortgage_amount);
    const mandate = toNumber(details.mortgage_mandate_amount);

    if (mortgage !== null && mortgage < 0) {
      fieldErrors.mortgage_amount = "validation.zeroOrGreater";
    }

    if (mandate !== null && mandate < 0) {
      fieldErrors.mortgage_mandate_amount = "validation.zeroOrGreater";
    }

    if (
      mortgage !== null &&
      mandate !== null &&
      approvedTotal !== null &&
      Math.abs(mortgage + mandate - approvedTotal) > 0.01
    ) {
      fieldErrors.mortgage_amount = "validation.mortgageSecurityMismatch";
      fieldErrors.mortgage_mandate_amount =
        "validation.mortgageSecurityMismatch";
    }
  }

  details.funding_sources.forEach((source, index) => {
    const hasAnyValue = Boolean(
      source.source_type || source.name || hasValue(source.amount),
    );

    if (!hasAnyValue) return;

    const errors = {};

    if (!["SUBSIDY", "SOFT_LOAN", "OTHER"].includes(source.source_type)) {
      errors.source_type = "validation.fundingTypeRequired";
    }

    if (!String(source.name || "").trim()) {
      errors.name = "validation.fundingNameRequired";
    }

    if (!hasValue(source.amount) || (toNumber(source.amount) || 0) <= 0) {
      errors.amount = "validation.fundingAmountRequired";
    }

    if (!["EXPECTED", "CONFIRMED"].includes(source.status)) {
      errors.status = "validation.fundingStatusRequired";
    }

    if (Object.keys(errors).length) {
      fundingSourceErrors[index] = errors;
    }
  });

  const supplementaryFundingTotal = details.funding_sources.reduce(
    (total, source) => total + (toNumber(source.amount) || 0),
    0,
  );

  const renovationFundingTotal =
    (renovationLoan || 0) + supplementaryFundingTotal;

  if (
    renovationBudget > 0 &&
    renovationFundingTotal > renovationBudget + 0.01
  ) {
    fieldWarnings.approved_renovation_loan_amount =
      "validation.renovationFundingAboveInitialEstimate";
  }

  const calculatedMonthly = calculateMonthlyPayment(
    details.approved_total_loan_amount,
    details.indicative_apr_pct,
    details.tenure_years,
  );

  const enteredMonthly = toNumber(details.estimated_monthly_payment);

  if (calculatedMonthly !== null && enteredMonthly !== null) {
    const tolerance = Math.max(1, calculatedMonthly * 0.02);

    if (Math.abs(calculatedMonthly - enteredMonthly) > tolerance) {
      fieldWarnings.estimated_monthly_payment =
        "validation.monthlyPaymentMismatch";
    }
  }

  return {
    fieldErrors,
    fieldWarnings,
    fundingSourceErrors,
    isValid:
      Object.keys(fieldErrors).length === 0 &&
      Object.keys(fundingSourceErrors).length === 0,
  };
}

/* =========================================================
   INPUT COMPONENTS
   ========================================================= */

function MoneyField({
  t,
  label,
  value,
  onChange,
  helper = "",
  placeholder = "",
  required = false,
  error = "",
  warning = "",
  readOnly = false,
}) {
  const inputRef = React.useRef(null);

  function handleChange(event) {
    if (readOnly) {
      return;
    }

    const input = event.target;

    const rawInput = input.value;

    const selectionStart = input.selectionStart ?? rawInput.length;

    const digitsBeforeCaret = rawInput
      .slice(0, selectionStart)
      .replace(/\D/g, "").length;

    const normalized = sanitizeMoneyInput(rawInput);

    onChange?.(normalized);

    requestAnimationFrame(() => {
      const element = inputRef.current;

      if (!element) {
        return;
      }

      const formatted = formatMoneyInput(normalized);

      let seenDigits = 0;
      let caret = formatted.length;

      for (let index = 0; index < formatted.length; index += 1) {
        if (/\d/.test(formatted[index])) {
          seenDigits += 1;
        }

        if (seenDigits >= digitsBeforeCaret) {
          caret = index + 1;

          break;
        }
      }

      while (formatted[caret] === ",") {
        caret += 1;
      }

      try {
        element.setSelectionRange(caret, caret);
      } catch {}
    });
  }

  return (
    <label
      className={`lender-final-loan-step__field ${
        error ? "lender-final-loan-step__field--invalid" : ""
      } ${warning ? "lender-final-loan-step__field--warning" : ""}`}
    >
      <span className="lender-final-loan-step__label">
        {label}

        {required ? (
          <span className="lender-final-loan-step__required">*</span>
        ) : null}
      </span>

      <div className="lender-final-loan-step__affixed-control">
        <span className="lender-final-loan-step__prefix">€</span>

        <input
          ref={inputRef}
          className={`lender-final-loan-step__input lender-final-loan-step__input--money ${
            readOnly ? "lender-final-loan-step__input--readonly" : ""
          }`}
          type="text"
          inputMode="decimal"
          value={formatMoneyInput(value)}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
        />
      </div>

      {helper ? (
        <span className="lender-final-loan-step__helper">{helper}</span>
      ) : null}

      {error ? (
        <span className="lender-final-loan-step__error">{t(error)}</span>
      ) : warning ? (
        <span className="lender-final-loan-step__warning">{t(warning)}</span>
      ) : null}
    </label>
  );
}

function StandardField({
  t,
  label,
  value,
  onChange,
  helper = "",
  placeholder = "",
  required = false,
  error = "",
  warning = "",
  kind = "text",
  name = "",
}) {
  function normalize(valueToNormalize) {
    if (PERCENT_FIELDS.has(name)) {
      return normalizeDecimal(valueToNormalize, 4);
    }

    if (INTEGER_FIELDS.has(name)) {
      return normalizeInteger(valueToNormalize);
    }

    return valueToNormalize;
  }

  return (
    <label
      className={`lender-final-loan-step__field ${
        error ? "lender-final-loan-step__field--invalid" : ""
      } ${warning ? "lender-final-loan-step__field--warning" : ""}`}
    >
      <span className="lender-final-loan-step__label">
        {label}
        {required ? (
          <span className="lender-final-loan-step__required">*</span>
        ) : null}
      </span>

      {kind === "percent" ? (
        <div className="lender-final-loan-step__affixed-control">
          <input
            className="lender-final-loan-step__input lender-final-loan-step__input--percent"
            type="text"
            inputMode="decimal"
            value={value || ""}
            onChange={(event) => onChange(normalize(event.target.value))}
            placeholder={placeholder}
          />
          <span className="lender-final-loan-step__suffix">%</span>
        </div>
      ) : (
        <input
          className="lender-final-loan-step__input"
          type="text"
          inputMode={INTEGER_FIELDS.has(name) ? "numeric" : undefined}
          value={value || ""}
          onChange={(event) => onChange(normalize(event.target.value))}
          placeholder={placeholder}
        />
      )}

      {helper ? (
        <span className="lender-final-loan-step__helper">{helper}</span>
      ) : null}

      {error ? (
        <span className="lender-final-loan-step__error">{t(error)}</span>
      ) : warning ? (
        <span className="lender-final-loan-step__warning">{t(warning)}</span>
      ) : null}
    </label>
  );
}

function TextareaField({
  label,
  value,
  onChange,
  helper = "",
  placeholder = "",
}) {
  return (
    <label className="lender-final-loan-step__field lender-final-loan-step__field--full">
      <span className="lender-final-loan-step__label">{label}</span>
      <textarea
        className="lender-final-loan-step__input lender-final-loan-step__textarea"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
      {helper ? (
        <span className="lender-final-loan-step__helper">{helper}</span>
      ) : null}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  error = "",
  t,
  disabled = false,
}) {
  return (
    <label
      className={`lender-final-loan-step__field ${
        error ? "lender-final-loan-step__field--invalid" : ""
      }`}
    >
      <span className="lender-final-loan-step__label">{label}</span>

      <div
        className="lender-final-loan-step__select-control"
        style={{ position: "relative" }}
      >
        <select
          className="lender-final-loan-step__input lender-final-loan-step__select"
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          style={{
            appearance: "none",
            WebkitAppearance: "none",
            paddingRight: "44px",
          }}
        >
          {options.map((option) => (
            <option
              key={`${option.value}-${option.label}`}
              value={option.value}
              disabled={Boolean(option.disabled)}
            >
              {option.label}
            </option>
          ))}
        </select>

        <ChevronDown
          size={17}
          strokeWidth={2}
          aria-hidden="true"
          className="lender-final-loan-step__select-chevron"
          style={{
            position: "absolute",
            top: "50%",
            right: "14px",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            color: "#667463",
          }}
        />
      </div>

      {error ? (
        <span className="lender-final-loan-step__error">{t(error)}</span>
      ) : null}
    </label>
  );
}

function Card({ icon: Icon, title, description, children, className = "" }) {
  return (
    <section className={`lender-final-loan-step__card ${className}`}>
      <header className="lender-final-loan-step__card-header">
        <span className="lender-final-loan-step__card-icon">
          <Icon size={19} strokeWidth={2.2} />
        </span>
        <div>
          <h3>{title}</h3>
          {description ? <p>{description}</p> : null}
        </div>
      </header>
      <div className="lender-final-loan-step__card-body">{children}</div>
    </section>
  );
}

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export default function LenderManualLoanCaseFinalLoanDetails() {
  const { t, i18n } = useTranslation("lender-final-loan-details");

  const {
    loanCaseData,
    loanCaseId,
    currentStepKey,
    stepValues,
    handleStepValidityChange,
    handleStepValuesChange,
  } = useOutletContext();

  const locale = React.useMemo(() => {
    const language = String(i18n.resolvedLanguage || i18n.language || "en")
      .split("-")[0]
      .toLowerCase();

    return (
      {
        en: "en-GB",
        es: "es-ES",
        ca: "ca-ES",
        nl: "nl-BE",
      }[language] || "en-GB"
    );
  }, [i18n.language, i18n.resolvedLanguage]);

  const financialInfo = React.useMemo(
    () => getFinancialInfo(loanCaseData, stepValues),
    [loanCaseData, stepValues],
  );

  const propertyInfo = React.useMemo(
    () => getPropertyInfo(loanCaseData, stepValues),
    [loanCaseData, stepValues],
  );

  const countryCode = React.useMemo(
    () => getCountryCode(loanCaseData, propertyInfo),
    [loanCaseData, propertyInfo],
  );

  const isBelgiumCase = countryCode === "BE";
  const marketRegionLabel = getMarketRegionLabel(loanCaseData);
  const marketRegionCode = getMarketRegionCode(loanCaseData);
  const isFlandersCase =
    isBelgiumCase &&
    (marketRegionCode === "FLANDERS" ||
      String(marketRegionLabel || "")
        .trim()
        .toUpperCase() === "FLANDERS");

  const lenderReference = getLenderReference(loanCaseData);

  const fundingCatalog = React.useMemo(
    () => getFundingCatalog(loanCaseData),
    [loanCaseData],
  );

  const contextPropertyPrice = getContextNumber(
    financialInfo,
    "property_purchase_price",
    "property_price",
  );

  const renovationBudget = getContextNumber(
    financialInfo,
    "estimated_renovation_budget",
    "available_renovation_budget",
    "renovation_budget",
  );

  const requestedPurchaseLoan = getContextNumber(
    financialInfo,
    "requested_purchase_loan_amount",
    "requested_purchase_financing",
  );

  const requestedRenovationLoan = getContextNumber(
    financialInfo,
    "requested_renovation_loan_amount",
    "requested_renovation_financing",
  );

  const requestedTotal = getContextNumber(
    financialInfo,
    "requested_total_financing_amount",
    "requested_total_loan_amount",
  );

  const contextOwnContribution = getContextNumber(
    financialInfo,
    "own_contribution_amount",
    "own_contribution",
  );

  const totalProjectCost = contextPropertyPrice + renovationBudget;

  const [details, setDetails] = React.useState(EMPTY_FINAL_LOAN_DETAILS);
  const [touched, setTouched] = React.useState({});
  const [monthlyPaymentOverridden, setMonthlyPaymentOverridden] =
    React.useState(false);

  const initializedCaseRef = React.useRef(null);
  const fundingCounterRef = React.useRef(0);

  React.useEffect(() => {
    if (!loanCaseData || !loanCaseId) return;
    if (initializedCaseRef.current === String(loanCaseId)) return;

    const storedStep = stepValues?.[currentStepKey] || {};
    const storedDetails = storedStep.final_loan_details || storedStep;
    const backendDetails =
      loanCaseData.final_loan_details || loanCaseData.result || {};

    const hasStoredDetails =
      isObject(storedDetails) && Object.keys(storedDetails).length > 0;

    const source = hasStoredDetails ? storedDetails : backendDetails;

    const backendFundingSources =
      source?.funding_sources ||
      storedStep?.funding_sources ||
      loanCaseData?.result?.funding_sources ||
      [];

    const normalized = normalizeFinalLoanDetails(source, backendFundingSources);

    if (!hasValue(normalized.property_price) && contextPropertyPrice > 0) {
      normalized.property_price = String(contextPropertyPrice);
    }

    if (
      !hasValue(normalized.own_contribution_amount) &&
      contextOwnContribution >= 0
    ) {
      normalized.own_contribution_amount = String(contextOwnContribution);
    }

    if (!hasValue(normalized.approved_total_loan_amount)) {
      const purchase = toNumber(normalized.approved_purchase_loan_amount);
      const renovation = toNumber(normalized.approved_renovation_loan_amount);

      if (purchase !== null || renovation !== null) {
        normalized.approved_total_loan_amount = String(
          (purchase || 0) + (renovation || 0),
        );
      }
    }

    if (!hasValue(normalized.estimated_monthly_payment)) {
      const monthly = calculateMonthlyPayment(
        normalized.approved_total_loan_amount,
        normalized.indicative_apr_pct,
        normalized.tenure_years,
      );

      normalized.estimated_monthly_payment =
        monthly === null ? "" : monthly.toFixed(2);
    }

    setDetails(normalized);
    setTouched({});
    setMonthlyPaymentOverridden(false);
    initializedCaseRef.current = String(loanCaseId);
  }, [
    contextOwnContribution,
    contextPropertyPrice,
    currentStepKey,
    loanCaseData,
    loanCaseId,
    stepValues,
  ]);

  const updateField = React.useCallback(
    (name, value) => {
      setTouched((previous) => ({
        ...previous,
        [name]: true,
      }));

      setDetails((previous) => {
        const next = {
          ...previous,
          decision: "APPROVED",
          [name]: value,
        };

        if (
          [
            "approved_purchase_loan_amount",
            "approved_renovation_loan_amount",
          ].includes(name)
        ) {
          const purchase = toNumber(next.approved_purchase_loan_amount) || 0;
          const renovation =
            toNumber(next.approved_renovation_loan_amount) || 0;
          const total = purchase + renovation;

          next.approved_total_loan_amount = total > 0 ? String(total) : "";
        }

        if (
          !monthlyPaymentOverridden &&
          [
            "approved_purchase_loan_amount",
            "approved_renovation_loan_amount",
            "indicative_apr_pct",
            "tenure_years",
          ].includes(name)
        ) {
          const monthly = calculateMonthlyPayment(
            next.approved_total_loan_amount,
            next.indicative_apr_pct,
            next.tenure_years,
          );

          next.estimated_monthly_payment =
            monthly === null ? "" : monthly.toFixed(2);
        }

        return next;
      });
    },
    [monthlyPaymentOverridden],
  );

  function updateMonthlyPayment(value) {
    setMonthlyPaymentOverridden(true);
    updateField("estimated_monthly_payment", value);
  }

  function recalculateMonthlyPayment() {
    const monthly = calculateMonthlyPayment(
      details.approved_total_loan_amount,
      details.indicative_apr_pct,
      details.tenure_years,
    );

    setMonthlyPaymentOverridden(false);
    updateField(
      "estimated_monthly_payment",
      monthly === null ? "" : monthly.toFixed(2),
    );
  }

  function addFundingSource() {
    fundingCounterRef.current += 1;

    const subsidyOptions = getFundingNameOptions({
      sourceType: "SUBSIDY",
      fundingCatalog,
      isFlandersCase,
    });

    setDetails((previous) => ({
      ...previous,
      funding_sources: [
        ...previous.funding_sources,
        {
          client_key: `new-${fundingCounterRef.current}`,
          source_type: "SUBSIDY",
          name: subsidyOptions.length === 1 ? subsidyOptions[0].value : "",
          amount: "",
          status: "EXPECTED",
        },
      ],
    }));
  }

  function updateFundingSource(index, field, value) {
    setDetails((previous) => ({
      ...previous,
      funding_sources: previous.funding_sources.map((source, sourceIndex) => {
        if (sourceIndex !== index) return source;

        if (field === "source_type") {
          const nameOptions = getFundingNameOptions({
            sourceType: value,
            fundingCatalog,
            isFlandersCase,
          });

          const currentNameIsStillValid = nameOptions.some(
            (option) => option.value === source.name,
          );

          return {
            ...source,
            source_type: value,
            name:
              value === "OTHER"
                ? ""
                : currentNameIsStillValid
                  ? source.name
                  : nameOptions.length === 1
                    ? nameOptions[0].value
                    : "",
          };
        }

        return {
          ...source,
          [field]:
            field === "amount" ? sanitizeFormattedNumber(value, 2) : value,
        };
      }),
    }));
  }

  function removeFundingSource(index) {
    setDetails((previous) => ({
      ...previous,
      funding_sources: previous.funding_sources.filter(
        (_source, sourceIndex) => sourceIndex !== index,
      ),
    }));
  }

  const validation = React.useMemo(
    () =>
      buildValidation({
        details,
        isBelgiumCase,
        renovationBudget,
        totalProjectCost,
      }),
    [details, isBelgiumCase, renovationBudget, totalProjectCost],
  );

  const serializedDetails = React.useMemo(
    () => serializeFinalLoanDetails(details),
    [details],
  );

  React.useEffect(() => {
    if (initializedCaseRef.current !== String(loanCaseId)) return;

    handleStepValidityChange(
      currentStepKey,
      validation.isValid,
      [
        ...Object.keys(validation.fieldErrors).map(name => t(`fields.${name}`, name.replace(/_/g, " "))),
        ...Object.values(validation.fundingSourceErrors).flatMap((fields, i) =>
          Object.keys(fields).map(name => `Funding source ${i + 1}: ${name.replace(/_/g, " ")}`)),
      ],
    );

    handleStepValuesChange(currentStepKey, {
      final_loan_details: serializedDetails,

      // Kept as a sibling too so the manual-entry layout can
      // optionally promote it to the finalize payload.
      funding_sources: serializedDetails.funding_sources,
    });
  }, [
    currentStepKey,
    handleStepValidityChange,
    handleStepValuesChange,
    loanCaseId,
    serializedDetails,
    validation.isValid,
    validation.fieldErrors,
    validation.fundingSourceErrors,
    t,
  ]);

  const visibleError = React.useCallback(
    (field) => (touched[field] ? validation.fieldErrors[field] || "" : ""),
    [touched, validation.fieldErrors],
  );

  const visibleWarning = React.useCallback(
    (field) => validation.fieldWarnings[field] || "",
    [validation.fieldWarnings],
  );

  const supplementaryFundingTotal = details.funding_sources.reduce(
    (total, source) => total + (toNumber(source.amount) || 0),
    0,
  );

  const confirmedSupplementaryFunding = details.funding_sources.reduce(
    (total, source) =>
      source.status === "CONFIRMED"
        ? total + (toNumber(source.amount) || 0)
        : total,
    0,
  );

  const lenderRenovationLoan =
    toNumber(details.approved_renovation_loan_amount) || 0;

  const potentialRenovationFunding =
    lenderRenovationLoan + supplementaryFundingTotal;

  const confirmedRenovationFunding =
    (details.renovation_financing_status === "CONFIRMED"
      ? lenderRenovationLoan
      : 0) + confirmedSupplementaryFunding;

  const renovationFundingGap =
    renovationBudget > 0
      ? Math.max(renovationBudget - potentialRenovationFunding, 0)
      : null;

  return (
    <div className="lender-final-loan-step">
      <div className="lender-final-loan-step__stack">
        {/* ===================================================
            APPROVED MANUAL CASE
            =================================================== */}

        <Card
          icon={FileCheck2}
          title={t("cards.caseOutcome.title", "Final loan agreement")}
          description={t(
            "cards.caseOutcome.description",
            "This manual-entry case records financing that was already agreed by the lender outside LEG-UP.",
          )}
          className="lender-final-loan-step__card--outcome"
        >
          <div className="lender-final-loan-step__outcome-status">
            <span className="lender-final-loan-step__approved-badge">
              <CheckCircle2 size={17} strokeWidth={2.4} />
              {t("options.decision.approved", "Approved")}
            </span>

            <div className="lender-final-loan-step__reference-block">
              <span className="lender-final-loan-step__reference-label">
                {t("fields.lenderReference", "Lender reference")}
              </span>
              <strong className="lender-final-loan-step__reference-value">
                <Hash size={15} />
                {lenderReference || "—"}
              </strong>
            </div>

            {marketRegionLabel ? (
              <span className="lender-final-loan-step__region-badge">
                {marketRegionLabel}
              </span>
            ) : null}
          </div>
        </Card>

        {/* ===================================================
            CONTEXT
            =================================================== */}

        <Card
          icon={Building2}
          title={t("cards.context.title", "Financing context")}
          description={t(
            "cards.context.description",
            "Reference values from the earlier manual-entry steps. These values help reconcile the final lender financing.",
          )}
        >
          <div className="lender-final-loan-step__context-grid">
            <div className="lender-final-loan-step__context-item">
              <span>{t("context.propertyPrice", "Property price")}</span>
              <strong>{formatCurrency(contextPropertyPrice, locale)}</strong>
            </div>

            <div className="lender-final-loan-step__context-item">
              <span>{t("context.renovationBudget", "Renovation budget")}</span>
              <strong>{formatCurrency(renovationBudget, locale)}</strong>
            </div>

            <div className="lender-final-loan-step__context-item">
              <span>
                {t("context.requestedPurchase", "Requested purchase financing")}
              </span>
              <strong>{formatCurrency(requestedPurchaseLoan, locale)}</strong>
            </div>

            <div className="lender-final-loan-step__context-item">
              <span>
                {t(
                  "context.requestedRenovation",
                  "Requested renovation financing",
                )}
              </span>
              <strong>{formatCurrency(requestedRenovationLoan, locale)}</strong>
            </div>

            <div className="lender-final-loan-step__context-item lender-final-loan-step__context-item--strong">
              <span>
                {t("context.requestedTotal", "Requested total financing")}
              </span>
              <strong>{formatCurrency(requestedTotal, locale)}</strong>
            </div>
          </div>
        </Card>

        {/* ===================================================
            LENDER FINANCING
            =================================================== */}

        <Card
          icon={BadgeEuro}
          title={t("cards.financingTerms.title", "Agreed lender financing")}
          description={t(
            "cards.financingTerms.description",
            "Record the financing provided directly by the lender. Supplementary renovation funding is recorded separately below.",
          )}
          className="lender-final-loan-step__card--financing"
        >
          <div className="lender-final-loan-step__fields lender-final-loan-step__fields--financing">
            <MoneyField
              t={t}
              locale={locale}
              label={t("fields.approvedPurchaseLoanAmount", "Purchase loan")}
              value={details.approved_purchase_loan_amount}
              onChange={(value) =>
                updateField("approved_purchase_loan_amount", value)
              }
              required
              placeholder="120,000"
              error={visibleError("approved_purchase_loan_amount")}
            />

            <MoneyField
              t={t}
              locale={locale}
              label={t(
                "fields.approvedRenovationLoanAmount",
                "Lender renovation loan",
              )}
              value={details.approved_renovation_loan_amount}
              onChange={(value) =>
                updateField("approved_renovation_loan_amount", value)
              }
              required
              placeholder="30,000"
              error={visibleError("approved_renovation_loan_amount")}
              helper={t(
                "helpers.lenderRenovationLoan",
                "This is only the renovation amount financed directly by the lender.",
              )}
            />

            <MoneyField
              t={t}
              locale={locale}
              label={t("fields.approvedTotalLoanAmount", "Total lender loan")}
              value={details.approved_total_loan_amount}
              onChange={() => {}}
              required
              readOnly
              helper={t(
                "helpers.totalLenderLoan",
                "Automatically calculated as purchase loan + lender renovation loan.",
              )}
              error={visibleError("approved_total_loan_amount")}
            />

            <MoneyField
              t={t}
              locale={locale}
              label={t("fields.propertyPrice", "Property purchase price")}
              value={details.property_price}
              onChange={(value) => updateField("property_price", value)}
              required
              placeholder="140,000"
              error={visibleError("property_price")}
            />

            <MoneyField
              t={t}
              locale={locale}
              label={t("fields.ownContributionAmount", "Own contribution")}
              value={details.own_contribution_amount}
              onChange={(value) =>
                updateField("own_contribution_amount", value)
              }
              required
              placeholder="20,000"
              error={visibleError("own_contribution_amount")}
            />

            <MoneyField
              t={t}
              locale={locale}
              label={t("fields.firstTrancheAmount", "First tranche amount")}
              value={details.first_tranche_amount}
              onChange={(value) => updateField("first_tranche_amount", value)}
              placeholder="15,000"
              error={visibleError("first_tranche_amount")}
            />

            <StandardField
              t={t}
              label={t("fields.tenureYears", "Financing term")}
              name="tenure_years"
              value={details.tenure_years}
              onChange={(value) => updateField("tenure_years", value)}
              required
              placeholder="30"
              helper={t("helpers.years", "Years")}
              error={visibleError("tenure_years")}
            />

            <StandardField
              t={t}
              label={t("fields.indicativeApr", "Indicative APR")}
              name="indicative_apr_pct"
              kind="percent"
              value={details.indicative_apr_pct}
              onChange={(value) => updateField("indicative_apr_pct", value)}
              required
              placeholder="4.25"
              error={visibleError("indicative_apr_pct")}
            />

            <div className="lender-final-loan-step__monthly-field">
              <MoneyField
                t={t}
                locale={locale}
                label={t(
                  "fields.estimatedMonthlyPayment",
                  "Estimated monthly payment",
                )}
                value={details.estimated_monthly_payment}
                onChange={updateMonthlyPayment}
                required
                placeholder="850"
                error={visibleError("estimated_monthly_payment")}
                warning={visibleWarning("estimated_monthly_payment")}
                helper={
                  monthlyPaymentOverridden
                    ? t(
                        "helpers.monthlyPaymentAdjusted",
                        "Manually adjusted. You can restore the indicative calculation.",
                      )
                    : t(
                        "helpers.monthlyPaymentCalculated",
                        "Calculated from the total lender loan, APR and term.",
                      )
                }
              />

              <button
                type="button"
                className="lender-final-loan-step__recalculate"
                onClick={recalculateMonthlyPayment}
              >
                <Calculator size={14} />
                {t("actions.recalculate", "Recalculate")}
              </button>
            </div>
          </div>
        </Card>

        {/* ===================================================
            RENOVATION FUNDING
            =================================================== */}

        <Card
          icon={PiggyBank}
          title={t("cards.renovationFunding.title", "Renovation funding")}
          description={t(
            "cards.renovationFunding.description",
            "Keep the lender renovation loan separate from subsidies, soft loans and other supplementary renovation funding.",
          )}
          className="lender-final-loan-step__card--funding"
        >
          <div className="lender-final-loan-step__renovation-status-grid">
            <SelectField
              t={t}
              label={t(
                "fields.renovationFinancingStatus",
                "Lender renovation financing status",
              )}
              value={details.renovation_financing_status}
              onChange={(value) =>
                updateField("renovation_financing_status", value)
              }
              options={[
                {
                  value: "ESTIMATED",
                  label: t("funding.estimated", "Estimated"),
                },
                {
                  value: "CONFIRMED",
                  label: t("funding.confirmed", "Confirmed"),
                },
              ]}
            />

            <div className="lender-final-loan-step__funding-status-help">
              <ShieldCheck size={17} />
              <p>
                {details.renovation_financing_status === "CONFIRMED"
                  ? t(
                      "funding.confirmedHelp",
                      "The lender renovation amount is treated as confirmed financing.",
                    )
                  : t(
                      "funding.estimatedHelp",
                      "The lender renovation amount remains preliminary and can be updated later when the detailed renovation information is available.",
                    )}
              </p>
            </div>
          </div>

          <div className="lender-final-loan-step__renovation-notes">
            <TextareaField
              label={t(
                "fields.renovationFinancingNotes",
                "Renovation financing notes",
              )}
              value={details.renovation_financing_notes}
              onChange={(value) =>
                updateField("renovation_financing_notes", value)
              }
              placeholder={t(
                "placeholders.renovationFinancingNotes",
                "Explain whether the renovation amount is final or still based on preliminary renovation information.",
              )}
            />
          </div>

          <div className="lender-final-loan-step__funding-summary">
            <div>
              <span>{t("funding.renovationBudget", "Renovation budget")}</span>
              <strong>{formatCurrency(renovationBudget, locale)}</strong>
            </div>
            <div>
              <span>{t("funding.lenderLoan", "Lender renovation loan")}</span>
              <strong>{formatCurrency(lenderRenovationLoan, locale)}</strong>
            </div>
            <div>
              <span>
                {t("funding.otherFunding", "Other renovation funding")}
              </span>
              <strong>
                {formatCurrency(supplementaryFundingTotal, locale)}
              </strong>
            </div>
            <div className="lender-final-loan-step__funding-summary--highlight">
              <span>
                {t("funding.potentialTotal", "Potential renovation funding")}
              </span>
              <strong>
                {formatCurrency(potentialRenovationFunding, locale)}
              </strong>
            </div>
            <div>
              <span>
                {t("funding.confirmedTotal", "Confirmed renovation funding")}
              </span>
              <strong>
                {formatCurrency(confirmedRenovationFunding, locale)}
              </strong>
            </div>
            <div>
              <span>{t("funding.gap", "Potential funding gap")}</span>
              <strong>
                {renovationFundingGap === null
                  ? "—"
                  : formatCurrency(renovationFundingGap, locale)}
              </strong>
            </div>
          </div>

          <div className="lender-final-loan-step__funding-sources">
            <div className="lender-final-loan-step__funding-sources-header">
              <div>
                <strong>
                  {t("funding.title", "Other renovation funding")}
                </strong>
                <p>
                  {t(
                    "funding.subtitleGeneric",
                    "Add subsidies, soft loans or other funding that contributes to the renovation but is separate from this lender loan.",
                  )}
                </p>
              </div>

              <button
                type="button"
                className="lender-final-loan-step__add-source"
                onClick={addFundingSource}
              >
                <Plus size={15} />
                {t("funding.addSource", "Add funding source")}
              </button>
            </div>

            {details.funding_sources.length === 0 ? (
              <div className="lender-final-loan-step__funding-empty">
                <Wallet size={18} />
                <span>
                  {t(
                    "funding.empty",
                    "No supplementary renovation funding sources have been recorded.",
                  )}
                </span>
              </div>
            ) : (
              <div className="lender-final-loan-step__funding-list">
                {details.funding_sources.map((source, index) => {
                  const rowErrors = validation.fundingSourceErrors[index] || {};

                  return (
                    <div
                      key={source.client_key}
                      className="lender-final-loan-step__funding-row"
                    >
                      <div className="lender-final-loan-step__funding-row-top">
                        <strong>
                          {t("funding.sourceNumber", {
                            number: index + 1,
                            defaultValue: "Funding source {{number}}",
                          })}
                        </strong>

                        <button
                          type="button"
                          className="lender-final-loan-step__remove-source"
                          onClick={() => removeFundingSource(index)}
                        >
                          <Trash2 size={14} />
                          {t("funding.remove", "Remove")}
                        </button>
                      </div>

                      <div className="lender-final-loan-step__funding-row-grid">
                        <SelectField
                          t={t}
                          label={t("funding.type", "Funding type")}
                          value={source.source_type}
                          onChange={(value) =>
                            updateFundingSource(index, "source_type", value)
                          }
                          options={[
                            {
                              value: "",
                              label: t("common.select", "Select"),
                            },
                            {
                              value: "SUBSIDY",
                              label: t("funding.types.subsidy", "Subsidy"),
                            },
                            {
                              value: "SOFT_LOAN",
                              label: t("funding.types.softLoan", "Soft loan"),
                            },
                            {
                              value: "OTHER",
                              label: t("funding.types.other", "Other"),
                            },
                          ]}
                          error={rowErrors.source_type}
                        />

                        {(() => {
                          const nameOptions = getFundingNameOptions({
                            sourceType: source.source_type,
                            fundingCatalog,
                            isFlandersCase,
                          });

                          if (
                            source.source_type === "OTHER" ||
                            !nameOptions.length
                          ) {
                            return (
                              <label
                                className={`lender-final-loan-step__field ${
                                  rowErrors.name
                                    ? "lender-final-loan-step__field--invalid"
                                    : ""
                                }`}
                              >
                                <span className="lender-final-loan-step__label">
                                  {t("funding.name", "Source name")}
                                </span>

                                <input
                                  className="lender-final-loan-step__input"
                                  type="text"
                                  value={source.name}
                                  onChange={(event) =>
                                    updateFundingSource(
                                      index,
                                      "name",
                                      event.target.value,
                                    )
                                  }
                                  placeholder={t(
                                    "funding.namePlaceholder",
                                    "Enter the funding source name",
                                  )}
                                />

                                {rowErrors.name ? (
                                  <span className="lender-final-loan-step__error">
                                    {t(rowErrors.name)}
                                  </span>
                                ) : null}
                              </label>
                            );
                          }

                          return (
                            <SelectField
                              t={t}
                              label={
                                source.source_type === "SUBSIDY"
                                  ? t(
                                      "funding.subsidyName",
                                      "Available subsidy",
                                    )
                                  : t(
                                      "funding.softLoanName",
                                      "Available soft loan",
                                    )
                              }
                              value={source.name}
                              onChange={(value) =>
                                updateFundingSource(index, "name", value)
                              }
                              options={[
                                {
                                  value: "",
                                  label:
                                    source.source_type === "SUBSIDY"
                                      ? t(
                                          "funding.selectSubsidy",
                                          "Select available subsidy",
                                        )
                                      : t(
                                          "funding.selectSoftLoan",
                                          "Select available soft loan",
                                        ),
                                },
                                ...nameOptions,
                              ]}
                              error={rowErrors.name}
                            />
                          );
                        })()}

                        <MoneyField
                          t={t}
                          locale={locale}
                          label={t("funding.amount", "Amount")}
                          value={source.amount}
                          onChange={(value) =>
                            updateFundingSource(index, "amount", value)
                          }
                          placeholder="15,000"
                          error={rowErrors.amount}
                        />

                        <SelectField
                          t={t}
                          label={t("funding.status", "Status")}
                          value={source.status}
                          onChange={(value) =>
                            updateFundingSource(index, "status", value)
                          }
                          options={[
                            {
                              value: "EXPECTED",
                              label: t("funding.expected", "Expected"),
                            },
                            {
                              value: "CONFIRMED",
                              label: t("funding.confirmed", "Confirmed"),
                            },
                          ]}
                          error={rowErrors.status}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {validation.fieldErrors.approved_renovation_loan_amount ===
            "validation.renovationFundingExceedsBudget" ? (
              <div className="lender-final-loan-step__funding-alert">
                <AlertTriangle size={16} />
                <span>{t("validation.renovationFundingExceedsBudget")}</span>
              </div>
            ) : null}
          </div>
        </Card>

        {/* ===================================================
            BELGIUM SECURITY
            =================================================== */}

        {isBelgiumCase ? (
          <Card
            icon={Landmark}
            title={t("cards.security.title", "Belgium mortgage security")}
            description={t(
              "cards.security.description",
              "For Belgian cases, the registered mortgage and mortgage mandate together must equal the total lender loan.",
            )}
            className="lender-final-loan-step__card--security"
          >
            <div className="lender-final-loan-step__fields lender-final-loan-step__fields--security">
              <MoneyField
                t={t}
                locale={locale}
                label={t("fields.mortgageAmount", "Mortgage amount")}
                value={details.mortgage_amount}
                onChange={(value) => updateField("mortgage_amount", value)}
                required
                placeholder="180,000"
                error={visibleError("mortgage_amount")}
              />

              <MoneyField
                t={t}
                locale={locale}
                label={t(
                  "fields.mortgageMandateAmount",
                  "Mortgage mandate amount",
                )}
                value={details.mortgage_mandate_amount}
                onChange={(value) =>
                  updateField("mortgage_mandate_amount", value)
                }
                required
                placeholder="46,000"
                error={visibleError("mortgage_mandate_amount")}
              />
            </div>

            <div className="lender-final-loan-step__security-summary">
              <ShieldCheck size={18} />
              <div>
                <span>{t("security.total", "Security total")}</span>
                <strong>
                  {formatCurrency(
                    (toNumber(details.mortgage_amount) || 0) +
                      (toNumber(details.mortgage_mandate_amount) || 0),
                    locale,
                  )}
                </strong>
              </div>
              <div>
                <span>{t("security.loan", "Total lender loan")}</span>
                <strong>
                  {formatCurrency(details.approved_total_loan_amount, locale)}
                </strong>
              </div>
            </div>
          </Card>
        ) : null}

        {/* ===================================================
            CONDITIONS AND NOTES
            =================================================== */}

        <Card
          icon={FileText}
          title={t("cards.conditions.title", "Conditions and lender notes")}
          description={t(
            "cards.conditions.description",
            "Record conditions attached to the approved financing and any internal lender notes.",
          )}
        >
          <div className="lender-final-loan-step__fields lender-final-loan-step__fields--notes">
            <TextareaField
              label={t("fields.additionalConditions", "Conditions")}
              value={details.conditions}
              onChange={(value) => updateField("conditions", value)}
              placeholder={t(
                "placeholders.additionalConditions",
                "e.g. Insurance requirements, additional documentation or disbursement conditions.",
              )}
            />

            <TextareaField
              label={t("fields.lenderNotes", "Internal lender notes")}
              value={details.notes}
              onChange={(value) => updateField("notes", value)}
              placeholder={t(
                "placeholders.lenderNotes",
                "Optional internal notes relevant to the approved loan.",
              )}
            />
          </div>
        </Card>

        {/* ===================================================
            VALIDATION SUMMARY
            =================================================== */}

        {!validation.isValid ? (
          <div className="lender-final-loan-step__validation-summary">
            <AlertTriangle size={18} />
            <div>
              <strong>
                {t(
                  "validation.summaryTitle",
                  "Complete the required financing information",
                )}
              </strong>
              <p>
                {t(
                  "validation.summaryText",
                  "Resolve the highlighted fields before continuing to the review step.",
                )}
              </p>
            </div>
          </div>
        ) : (
          <div className="lender-final-loan-step__validation-summary lender-final-loan-step__validation-summary--valid">
            <CheckCircle2 size={18} />
            <div>
              <strong>
                {t("validation.readyTitle", "Final loan details complete")}
              </strong>
              <p>
                {t(
                  "validation.readyText",
                  "The lender financing and supplementary funding structure are ready for review.",
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
